import type { PlanTypeCard as PlanTypeCardData } from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, Grid, VStack } from '@enechain/ecloud-designsystem'

import { PlanTypeCard } from '~/pages/SubmissionManagement/components/PlanTypeCard'
import {
  type KnownPlanTypeCardKind,
  PLAN_TYPE_CARD_COLUMN,
  isKnownPlanTypeCardKind,
} from '~/pages/SubmissionManagement/models/planTypeCard'
import {
  type SubmissionStatusFilterOption,
  filterCardsByStatus,
} from '~/pages/SubmissionManagement/models/submissionStatusFilter'

/** `isKnownPlanTypeCardKind` を card 単位の type guard に持ち上げる (filter の型の絞り込みを保つため)。 */
function isKnownPlanTypeCard(
  card: PlanTypeCardData,
): card is PlanTypeCardData & { kind: KnownPlanTypeCardKind } {
  return isKnownPlanTypeCardKind(card.kind)
}

type Props = {
  /**
   * 選択中エリア・対象日で有効なカードの集合 (AC-PSM-006 で出す、提出ステータスの
   * 絞込〈§8〉の前のカード。kind + plan_records + submission_summary)。並び順は BFF 応答の順序をそのまま使う — proto
   * `SubmissionDashboardArea.cards` は PlanTypeCardKind の enum 順で返す契約
   * (doc comment 参照) のため、frontend 側で並べ直さない (review 指摘: 並び順の
   * 正を backend の契約に一本化)。
   */
  cards: readonly PlanTypeCardData[]
  /** 提出ステータスの絞込 (AC-PSM-070)。絞込は表示するカードだけに効き、列の組み方は変えない。 */
  statusFilter: SubmissionStatusFilterOption
  /** `cards` を取得した対象日 (取引先カードの遷移先に使う)。 */
  targetDate: Date
}

/**
 * 計画タイプカード grid (design `#plan-type-grid`)。
 *
 * 左列 (需要調達系) と右列 (発電販売系 + 分割供給) の 2 カラム。絞込前の
 * カードが片側の列にしか無い時は 2 カラムにせず全幅で 1 列に並べる (AC-PSM-007)。本 map は
 * 需要調達計画だけが対象で右列の種別がまだ無いため、現状は常に全幅になる
 * (右列は発電販売計画の実提出リリース時に追加、map #8431 Out of scope)。
 * 各列内の並びは BFF 応答順 (= enum 順、Decision 6)。UNSPECIFIED は防御的に落とす
 * (通常到達しない — proto は業務データとして UNSPECIFIED を送らない契約)。
 */
export const PlanTypeCardGrid = ({
  cards,
  statusFilter,
  targetDate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const knownCards = filterCardsByStatus(cards, statusFilter).filter(
    isKnownPlanTypeCard,
  )

  if (knownCards.length === 0) {
    // Decision 15: エリアはあるが対象日に有効カードが 0 枚 → grid の位置に短い
    // 案内文だけ出す (spec 未定義・design にも面が無いため新部品を作らない)。
    // 文言は絞込前の有効カードが 0 枚なら絞込の選択に関係なく AC-PSM-006、
    // 有効カードがあって絞込で 0 件なら AC-PSM-074。
    return (
      <Box
        padding="40px 20px"
        color="Text.midEmphasis"
        fontSize="md"
        textAlign="center"
      >
        {t(
          cards.length === 0
            ? 'page:submissionManagement.grid.noCards'
            : 'page:submissionManagement.statusFilter.empty',
        )}
      </Box>
    )
  }

  const layoutColumns = new Set(
    cards
      .filter(isKnownPlanTypeCard)
      .map((card) => PLAN_TYPE_CARD_COLUMN[card.kind]),
  )
  const renderColumn = (
    columnCards: ReadonlyArray<
      PlanTypeCardData & { kind: KnownPlanTypeCardKind }
    >,
  ): React.ReactElement => (
    <VStack alignItems="stretch" gap={4}>
      {columnCards.map((card) => (
        <PlanTypeCard
          key={card.kind}
          kind={card.kind}
          planRecords={card.planRecords}
          submissionSummary={card.submissionSummary}
          targetDate={targetDate}
        />
      ))}
    </VStack>
  )

  if (layoutColumns.size < 2) {
    return renderColumn(knownCards)
  }

  return (
    <Grid gridRowGap={6} gridColumnGap={10} gridTemplateColumns="1fr 1fr">
      {renderColumn(
        knownCards.filter(
          (card) => PLAN_TYPE_CARD_COLUMN[card.kind] === 'left',
        ),
      )}
      {renderColumn(
        knownCards.filter(
          (card) => PLAN_TYPE_CARD_COLUMN[card.kind] === 'right',
        ),
      )}
    </Grid>
  )
}
